import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { CompanyAccessModel } from '../../../../DataModels/company-access-model';
import {CompanyModel} from '../../../../DataModels/company-model';
import {SysUserModel} from '../../../../DataModels/sys-user-model';
import { NotificationService } from '../../../../Services/notification.service';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {CanComponentDeactivate} from '../../../../Interfaces/unsaved-changes.interface';
import {UnsavedChangesComponent} from '../../../unsaved-changes/unsaved-changes/unsaved-changes.component';
import {NavigationService} from '../../../../Services/navigation.service';

@Component({
  selector: 'app-company-access-form',
  templateUrl: './company-access-form.component.html',
  styleUrls: ['./company-access-form.component.css'],
  standalone: false
})
export class CompanyAccessFormComponent implements OnInit, AfterViewInit, CanComponentDeactivate {
  @ViewChild('unsavedChanges') unsavedChanges!: UnsavedChangesComponent;
  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  companyAccess: CompanyAccessModel;
  companyAccessForm: UntypedFormGroup;
  companyAccessID;

  // Unsaved changes tracking
  originalFormData: any = {};

  companyList: CompanyModel[];
  userList: SysUserModel[];

  buttonsDisabled = false;
  batchlocked = 0;
  paid = false;
  showDelete = false;
  submitted = false;

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title,
    private nav: NavigationService
  ) {
    this.createForm();
    this.companyAccessID = this.route.snapshot.params.id;
  }

  createForm() {
    this.companyAccessForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      user_id: new UntypedFormControl(''),
      company_id: new UntypedFormControl(''),
    });
  }

  ngOnInit() {
    this.loadCompanies();
    this.LoadUsers();
    if (this.companyAccessID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    }
  }

  ngAfterViewInit() {
    this.titleService.setTitle($localize`:Company Access @@companyAccess:Company Access`);
    // Establish baseline for new records after view initializes
    if (!this.companyAccessID) {
      this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
    }
  }

  get f() {
    return this.companyAccessForm.controls;
  }

  private LoadData() {
    this.table.getData('company_access', this.companyAccessID).subscribe(
      (result) => {
        this.companyAccess = result;
      },
      (error: HttpErrorResponse) => {
        // this.notification.showErrorToast($localize`:Error loading Companies @@errorLoadingCompanies:Error loading Companies`);
        console.log(error);
      },
      () => {
        this.companyAccessForm.patchValue(this.companyAccess);
        this.titleService.setTitle($localize`:Company Access @@companyAccess:Company Access`);
        // Establish baseline for Unsaved Changes guard
        this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
      }
    );
  }

  private loadCompanies() {
    this.table
      .getApiListData('company', '?pageNumber=0&pageSize=999999')
      .subscribe({
        next: (result: any) => {
          this.companyList = result.Data;
          // this.companyListLength = this.companyList.length;
        },
        error: (error: HttpErrorResponse) => {
          // this.notification.showErrorToast($localize`:Error loading Companies @@errorLoadingCompanies:Error loading Companies`);
          console.log(error);
        },
        complete: () => {
        }
      });
  }

  private LoadUsers() {
    this.table
      .getApiListData('sys_user', '?pageNumber=0&pageSize=999999')
      .subscribe({
        next: (result: any) => {
          this.userList = result.Data;
          // this.companyListLength = this.companyList.length;
        },
        error: (error: HttpErrorResponse) => {
          // this.notification.showErrorToast($localize`:Error loading users @@errorLoadingUsers:Error loading users`);
          console.log(error);
        },
        complete: () => {
        }
      });
  }
  saveClose() {
    if (this.onSave('saveClose')) {
    }
  }

  saveNew() {
    if (this.onSave('saveNew')) {
      this.resetForm();
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete company access? @@areYouSureYouWantToDeleteCompanyAccess:Are you sure you want to delete company access?`,
      this.companyAccessForm.get('user_id').value,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table
          .deleteDataById('company_access', this.companyAccessID)
          .subscribe(
            (result) => {
              retMsg = result;
              // this.notification.success($localize`:Deleted @@deleted:Deleted`);
            },
            (error: HttpErrorResponse) => {
              // this.notification.showErrorToast($localize`:Error @@error:Error`);
              console.log(error);
            },
            () => {
              this.onClose();
            }
          );
      },
      () => {
        this.notification.showSuccessToast(
          $localize`:Delete Canceled @@deleteCanceled:Delete Canceled`
        );
      }
    );
  }

  resetForm() {
    this.companyAccessForm.reset();
    // After an explicit reset, treat current state as clean
    this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
    if (this.unsavedChanges) {
      this.unsavedChanges.resetChanges();
    }
  }

  onClose() {
    const fallback = '/admin/company-access-list';
    this.nav.backOr(fallback);
  }

  // Unified save handler with modes
  onSave(mode: 'save' | 'saveClose' | 'saveNew') {
    this.submitted = true;
    if (this.companyAccessForm.invalid) {
      this.notification.showErrorToast(
        $localize`:Form Errors! @@formErrors:Form Errors!`
      );
      return;
    }
    this.companyAccess = Object.assign({}, this.companyAccessForm.value);
    if (this.companyAccessID) {
      // TODO probably can move invalid out side if and capture both
      if (this.companyAccessForm.invalid === false) {
        this.table
          .patch('company_access', this.companyAccessID, this.companyAccess)
          .then(
            (result) => {
              // Reset baseline to saved state
              this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
              if (this.unsavedChanges) {
                this.unsavedChanges.resetChanges();
              }
              if (mode === 'saveClose') {
                this.onClose();
              } else if (mode === 'saveNew') {
                // Prepare a fresh form for a new record
                this.companyAccessID = undefined;
                this.companyAccessForm.reset();
                // After save-new, consider the fresh form as clean
                this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
                // Remove any id from the URL completely
                this.router.navigate(['/admin/company-access-form'], {
                  replaceUrl: true
                }).then(() => {
                });
                // Ensure that if the user clicks Close next, we return to the list (fallback)
                this.nav.forceNextBackToFallback();
              }
            },
            (error: HttpErrorResponse) => {
              //
              console.log(error);
            }
          );
        // return true;
      }
    } else {
      this.table
        .postWithPromise('company_access', this.companyAccess)
        .then((res) => {
          // stops double posting when clicking Save
          let apiReturn: any = null;
          if (typeof res === 'object') {
            apiReturn = JSON.parse(JSON.stringify(res));
          }
          this.companyAccessID = apiReturn.id;
          if (mode === 'saveClose') {
            // Keep values, then close
            this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
            if (this.unsavedChanges) {
              this.unsavedChanges.resetChanges();
            }
            this.onClose();
          } else if (mode === 'saveNew') {
            // Create complete; start a brand-new blank record
            this.companyAccessID = undefined;
            this.companyAccessForm.reset();
            this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
            if (this.unsavedChanges) {
              this.unsavedChanges.resetChanges();
            }
            // Remove params from URL entirely for a new record state
            this.router.navigate(['/admin/company-access-form'], {
              replaceUrl: true
            }).then(() => {
            });
            // After Save New, if user clicks Close, always go to the list (fallback) once
            this.nav.forceNextBackToFallback();
          } else { // mode === 'save'
            // Stay on form with saved values and new ID
            this.originalFormData = JSON.parse(JSON.stringify(this.companyAccessForm.getRawValue()));
            if (this.unsavedChanges) {
              this.unsavedChanges.resetChanges();
            }
          }
        });

      // this.notification.success($localize`:Saved @@saved:Saved`);
      // return true;
    }
    this.submitted = false;
    return true;
  }

  // Implementation for UnsavedChangesGuard
  checkForChanges(): boolean {
    try {
      const current = this.companyAccessForm?.getRawValue?.() ?? {};
      return JSON.stringify(this.originalFormData) !== JSON.stringify(current);
    } catch {
      return false;
    }
  }
}
